import { useRef, useEffect } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
// @ts-ignore
import { useContentToken, EditableToken } from '@vibesites/runtime/react';
// @ts-ignore
import { PersonDiggingIcon, PeopleRoofIcon, BuildingUserIcon } from '@vibesites/assets/icons/ui';
import '../styles/company-what-we-do.css';

export function CompanyWhatWeDo() {
  const sectionTitle = useContentToken('companywhatwedo.section_title');
  const card1Title = useContentToken('companywhatwedo.card1_title');
  const card1Text = useContentToken('companywhatwedo.card1_text');
  const card2Title = useContentToken('companywhatwedo.card2_title');
  const card2Text = useContentToken('companywhatwedo.card2_text');
  const card3Title = useContentToken('companywhatwedo.card3_title');
  const card3Text = useContentToken('companywhatwedo.card3_text');

  const decorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrapper = decorRef.current;
    if (!wrapper) return;
    const svg = wrapper.querySelector('svg');
    if (!svg) return;

    type Vec3 = [number, number, number];
    const V: Vec3[] = [
      [-1,-1,-1],[1,-1,-1],[1,1,-1],[-1,1,-1],
      [-1,-1,1],[1,-1,1],[1,1,1],[-1,1,1],
    ];
    const E: [number,number][] = [
      [0,1],[1,2],[2,3],[3,0],
      [4,5],[5,6],[6,7],[7,4],
      [0,4],[1,5],[2,6],[3,7],
    ];
    const FACES: number[][] = [
      [0,1,2,3],[7,6,5,4],[0,3,7,4],[1,5,6,2],[3,2,6,7],[0,4,5,1],
    ];
    const EDGE_FACES: [number,number][] = [
      [0,5],[0,3],[0,4],[0,2],[1,5],[1,3],[1,4],[1,2],[2,5],[3,5],[3,4],[2,4],
    ];

    const INIT_RY = Math.PI / 4;
    const INIT_RX = -1.1261;
    const SCALE = 50.2, CX = 75, CY = 85;

    function applyRotY(v: Vec3, a: number): Vec3 {
      const c = Math.cos(a), s = Math.sin(a);
      return [v[0]*c + v[2]*s, v[1], -v[0]*s + v[2]*c];
    }
    function applyRotX(v: Vec3, a: number): Vec3 {
      const c = Math.cos(a), s = Math.sin(a);
      return [v[0], v[1]*c - v[2]*s, v[1]*s + v[2]*c];
    }
    function project(v: Vec3): [number, number] {
      return [CX + v[0] * SCALE, CY - v[1] * SCALE];
    }

    const NS = 'http://www.w3.org/2000/svg';
    const lines = E.map(() => {
      const ln = document.createElementNS(NS, 'line');
      ln.setAttribute('stroke', 'currentColor');
      ln.setAttribute('stroke-width', '2.4');
      ln.setAttribute('stroke-linecap', 'round');
      svg.appendChild(ln);
      return ln;
    });

    const TRIM_EDGES: Record<number, number> = {
      0: 1,
      2: 1,
      5: 1,
      10: 0,
    };
    const TRIM = 0.18;

    function render() {
      const rect = wrapper.getBoundingClientRect();
      const offset = rect.top + rect.height / 2 - window.innerHeight / 2;
      const ry = INIT_RY - offset * 0.000345;
      const rx = INIT_RX;
      const pts = V.map(v => project(applyRotX(applyRotY(v, ry), rx)));

      const faceVis = FACES.map(face => {
        const [a, b, c] = [pts[face[0]], pts[face[1]], pts[face[2]]];
        return (b[0]-a[0])*(c[1]-a[1]) - (b[1]-a[1])*(c[0]-a[0]) < 0;
      });

      const redAmount = Math.min(1, Math.abs(offset) / 800);

      E.forEach(([a, b], i) => {
        const visible = faceVis[EDGE_FACES[i][0]] || faceVis[EDGE_FACES[i][1]];
        let x1 = pts[a][0], y1 = pts[a][1];
        let x2 = pts[b][0], y2 = pts[b][1];

        if (i in TRIM_EDGES) {
          if (TRIM_EDGES[i] === 0) {
            x1 += (x2 - x1) * TRIM;
            y1 += (y2 - y1) * TRIM;
          } else {
            x2 += (x1 - x2) * TRIM;
            y2 += (y1 - y2) * TRIM;
          }
        }

        if (i % 2 === 0) {
          const r = Math.round(204 + (237 - 204) * redAmount);
          const g = Math.round(204 - 204 * redAmount);
          const b2 = Math.round(204 - (204 - 36) * redAmount);
          lines[i].setAttribute('stroke', `rgb(${r},${g},${b2})`);
        } else {
          lines[i].setAttribute('stroke', 'currentColor');
        }

        lines[i].setAttribute('x1', x1.toFixed(2));
        lines[i].setAttribute('y1', y1.toFixed(2));
        lines[i].setAttribute('x2', x2.toFixed(2));
        lines[i].setAttribute('y2', y2.toFixed(2));
        lines[i].style.opacity = visible ? '1' : '0';
      });
    }

    render();
    const onScroll = () => render();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      lines.forEach(ln => ln.parentNode?.removeChild(ln));
    };
  }, []);

  return (
    <section className="case-features" style={{ overflow: 'hidden' }}>
      <div
        ref={decorRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '-80px',
          top: '-40px',
          height: '500px',
          width: 'auto',
          opacity: 0.8,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="-1 -6 152 181"
          width="152"
          height="181"
          style={{ overflow: 'visible', height: '100%', width: 'auto', color: 'rgba(255,255,255,0.5)' }}
        />
      </div>
      <div className="case-container" style={{ position: 'relative', zIndex: 1 }}>
        <EditableToken path="companywhatwedo.section_title" type="text" label="Section Title" defaultValue={contentSchema.tokens['companywhatwedo.section_title'].defaultValue} as="h2" className="case-features-label">
          {sectionTitle}
        </EditableToken>
        <div className="case-features-grid">
          <div className="case-feature-card">
            <div className="case-feature-card-body">
              <div className="case-feature-card-icon"><PersonDiggingIcon color="currentColor" /></div>
              <EditableToken path="companywhatwedo.card1_title" type="text" defaultValue={contentSchema.tokens['companywhatwedo.card1_title'].defaultValue} as="h2">
                {card1Title}
              </EditableToken>
              <EditableToken path="companywhatwedo.card1_text" type="text" defaultValue={contentSchema.tokens['companywhatwedo.card1_text'].defaultValue} as="p">
                {card1Text}
              </EditableToken>
            </div>
          </div>
          <div className="case-feature-card">
            <div className="case-feature-card-body">
              <div className="case-feature-card-icon"><PeopleRoofIcon color="currentColor" /></div>
              <EditableToken path="companywhatwedo.card2_title" type="text" defaultValue={contentSchema.tokens['companywhatwedo.card2_title'].defaultValue} as="h2">
                {card2Title}
              </EditableToken>
              <EditableToken path="companywhatwedo.card2_text" type="text" defaultValue={contentSchema.tokens['companywhatwedo.card2_text'].defaultValue} as="p">
                {card2Text}
              </EditableToken>
            </div>
          </div>
          <div className="case-feature-card">
            <div className="case-feature-card-body">
              <div className="case-feature-card-icon"><BuildingUserIcon color="currentColor" /></div>
              <EditableToken path="companywhatwedo.card3_title" type="text" defaultValue={contentSchema.tokens['companywhatwedo.card3_title'].defaultValue} as="h2">
                {card3Title}
              </EditableToken>
              <EditableToken path="companywhatwedo.card3_text" type="text" defaultValue={contentSchema.tokens['companywhatwedo.card3_text'].defaultValue} as="p">
                {card3Text}
              </EditableToken>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CompanyWhatWeDo',
  displayName: 'What We Do',
  tokens: {
    'companywhatwedo.section_title': {
      label: 'Section Title',
      type: 'text',
      defaultValue: 'What We Do',
    },
    'companywhatwedo.card1_title': {
      label: 'Card 1 Title',
      type: 'text',
      defaultValue: 'Build',
    },
    'companywhatwedo.card1_text': {
      label: 'Card 1 Description',
      type: 'text',
      defaultValue: 'From the ground up, we design and construct our building projects to the highest standards of quality.',
    },
    'companywhatwedo.card2_title': {
      label: 'Card 2 Title',
      type: 'text',
      defaultValue: 'Manage',
    },
    'companywhatwedo.card2_text': {
      label: 'Card 2 Description',
      type: 'text',
      defaultValue: "Apartment life is the good life. We're committed to providing the best apartment experiences around.",
    },
    'companywhatwedo.card3_title': {
      label: 'Card 3 Title',
      type: 'text',
      defaultValue: 'Maintain',
    },
    'companywhatwedo.card3_text': {
      label: 'Card 3 Description',
      type: 'text',
      defaultValue: 'Award-winning maintenance and attention to detail stands out to our residents and our peers.',
    },
  },
};

(CompanyWhatWeDo as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default CompanyWhatWeDo;
